// src/screens/admin/AdminMeetings.js
import React from 'react';
import { FaCopy, FaTrash, FaUser, FaCalendarAlt, FaEye } from 'react-icons/fa';
import { useGetMeetingsQuery, useDeleteMeetingMutation } from '../../slices/meetingSlice';

const AdminMeetings = () => {
  const { 
    data: meetingsData, 
    isLoading: meetingsLoading, 
    error: meetingsError,
    refetch 
  } = useGetMeetingsQuery();
  
  const [deleteMeeting, { isLoading: deleteMeetingLoading }] = useDeleteMeetingMutation();

  const meetings = meetingsData?.data || [];

  const handleDeleteMeeting = async (id, meetingName) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le meeting "${meetingName}" ?`)) {
      try {
        await deleteMeeting(id).unwrap();
        refetch();
        alert('Meeting supprimé avec succès!');
      } catch (error) {
        console.error('Error deleting meeting:', error);
        alert('Erreur lors de la suppression du meeting');
      }
    }
  };

  const copyToClipboard = (link) => {
    navigator.clipboard.writeText(link);
    alert('Link copied to clipboard!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (meetingsLoading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Chargement de tous les meetings...</p>
        </div>
      </div>
    );
  }

  if (meetingsError) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          Erreur lors du chargement des meetings: {meetingsError.data?.message || 'Erreur serveur'}
        </div>
      </div>
    );
  }

  return (
    <div className='container mt-4'>
      <div className='card shadow-sm'>
        <div className='card-header bg-primary text-white'>
          <div className='d-flex justify-content-between align-items-center'>
            <h4 className='mb-0'>
              <FaEye className='me-2' />
              Tous les Meetings - Administration
            </h4>
            <span className='badge bg-light text-dark'>
              {meetings.length} meeting{meetings.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className='card-body'>
          {meetings.length === 0 ? (
            <div className="text-center text-muted py-5">
              <FaCalendarAlt size={48} className="mb-3" />
              <h5>Aucun meeting programmé</h5>
              <p>Aucun formateur n'a encore créé de meeting.</p>
            </div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-striped table-hover'>
                <thead className='table-dark'>
                  <tr>
                    <th scope='col'>Créé par</th>
                    <th scope='col'>Event Name</th>
                    <th scope='col'>Date</th>
                    <th scope='col'>Heure Début</th>
                    <th scope='col'>Heure Fin</th>
                    <th scope='col'>Meeting Link</th>
                    <th scope='col'>Créé le</th>
                    <th scope='col'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.map((meeting) => (
                    <tr key={meeting._id}>
                      <td>
                        <div className='d-flex align-items-center'>
                          <FaUser className='text-muted me-2' />
                          <small>
                            <strong>{meeting.createdBy}</strong>
                          </small>
                        </div>
                      </td>
                      <td>
                        <strong>{meeting.name}</strong>
                      </td>
                      <td>
                        <strong>{formatDate(meeting.datetime)}</strong>
                      </td>
                      <td>
                        <span className='badge bg-primary fs-6'>{meeting.startTime}</span>
                      </td>
                      <td>
                        <span className='badge bg-secondary fs-6'>{meeting.endTime}</span>
                      </td>
                      <td>
                        <div className='input-group input-group-sm'>
                          <input
                            type='text'
                            className='form-control form-control-sm'
                            value={meeting.link}
                            readOnly
                            style={{ fontSize: '0.75rem' }}
                          />
                          <button
                            onClick={() => copyToClipboard(meeting.link)}
                            className='btn btn-outline-secondary btn-sm'
                            type='button'
                            title='Copier le lien'
                          >
                            <FaCopy />
                          </button>
                        </div>
                      </td>
                      <td>
                        <small className='text-muted'>
                          {formatDateTime(meeting.createdAt)}
                        </small>
                      </td>
                      <td>
                        <button
                          onClick={() => handleDeleteMeeting(meeting._id, meeting.name)}
                          className='btn btn-outline-danger btn-sm'
                          title='Supprimer le meeting'
                          disabled={deleteMeetingLoading}
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className='card-footer text-muted'>
          <div className='row'>
            <div className='col-md-6'>
              <small>
                Dernière mise à jour: {new Date().toLocaleString('fr-FR')}
              </small>
            </div>
            <div className='col-md-6 text-end'>
              <small>
                Total: {meetings.length} meeting{meetings.length !== 1 ? 's' : ''}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMeetings;